import React from 'react';
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';
import OrganizationCard from './OrganizationCard';
import type { DiscoveredOrganization } from '../../redux/slices/organizationSlice';

interface OrganizationListProps {
    organizations: DiscoveredOrganization[];
    currentOrganizationId: string | null;
    onSwitch: (organizationId: string) => void;
    disabled?: boolean;
}

/**
 * OrganizationList - Presentational Component
 * Displays a list of organizations
 * Props: organizations array, currentOrganizationId, onSwitch callback
 */
const OrganizationList: React.FC<OrganizationListProps> = ({
    organizations,
    currentOrganizationId,
    onSwitch,
    disabled = false
}) => {
    const { t } = useTranslation();

    // Filter out current organization and show others
    const otherOrganizations = organizations.filter(
        org => org.organization_id !== currentOrganizationId
    );

    // Empty state
    if (otherOrganizations.length === 0) {
        return (
            <div className="text-center py-8">
                <Building2 className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-muted-foreground mb-2">
                    {t('organization.switcher.noOrganizations')}
                </h3>
                <p className="text-sm text-muted-foreground">
                    {t('organization.switcher.noOrganizationsDescription')}
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <h3 className="text-sm font-medium text-muted-foreground mb-3">
                {t('organization.switcher.availableOrganizations')}
            </h3>
            {otherOrganizations.map((org) => (
                <OrganizationCard
                    key={org.organization_id}
                    organization={org}
                    isCurrentOrganization={false}
                    onSwitch={onSwitch}
                    disabled={disabled}
                />
            ))}
        </div>
    );
};

export default OrganizationList;
